import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

export interface Encaissement {
  id: number;
  facture_id: number;
  montant: number;
  mode_paiement: 'especes' | 'carte' | 'virement' | 'cheque' | 'mobile';
  date_encaissement: string;
  notes?: string;
  created_at: string;
}

interface FacturesState {
  factures: any[];
  impayees: any[];
  currentFacture: any | null;
  encaissements: Encaissement[];
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  } | null;
}

const initialState: FacturesState = {
  factures: [],
  impayees: [],
  currentFacture: null,
  encaissements: [],
  loading: false,
  error: null,
  pagination: null,
};

// Actions asynchrones
export const fetchFactures = createAsyncThunk(
  'factures/fetchAll',
  async (params: { page?: number; limit?: number; search?: string; statut?: string; client_id?: number }) => {
    const response = await api.get('/factures', { params });
    return response.data.data;
  }
);

export const fetchImpayees = createAsyncThunk(
  'factures/fetchImpayees',
  async () => {
    const response = await api.get('/factures/impayees');
    return response.data.data;
  }
);

export const fetchFactureById = createAsyncThunk(
  'factures/fetchById',
  async (id: number) => {
    const response = await api.get(`/factures/${id}`);
    return response.data.data;
  }
);

export const createFacture = createAsyncThunk(
  'factures/create',
  async (factureData: any) => {
    const response = await api.post('/factures', factureData);
    return response.data;
  }
);

export const createEncaissement = createAsyncThunk(
  'factures/createEncaissement',
  async ({ factureId, data }: { factureId: number; data: Partial<Encaissement> }) => {
    const response = await api.post(`/factures/${factureId}/encaissements`, data);
    return response.data;
  }
);

export const deleteFacture = createAsyncThunk(
  'factures/delete',
  async (id: number) => {
    await api.delete(`/factures/${id}`);
    return id;
  }
);

// Slice
const facturesSlice = createSlice({
  name: 'factures',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentFacture: (state) => {
      state.currentFacture = null;
      state.encaissements = [];
    },
  },
  extraReducers: (builder) => {
    // Fetch all factures
    builder.addCase(fetchFactures.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchFactures.fulfilled, (state, action) => {
      state.loading = false;
      state.factures = action.payload.factures;
      state.pagination = action.payload.pagination;
    });
    builder.addCase(fetchFactures.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Erreur lors du chargement des factures';
    });

    // Fetch impayées
    builder.addCase(fetchImpayees.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchImpayees.fulfilled, (state, action) => {
      state.loading = false;
      state.impayees = action.payload;
    });
    builder.addCase(fetchImpayees.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Erreur lors du chargement des factures impayées';
    });

    // Fetch facture by ID
    builder.addCase(fetchFactureById.fulfilled, (state, action) => {
      state.currentFacture = action.payload.facture;
      state.encaissements = action.payload.encaissements;
    });

    // Delete facture
    builder.addCase(deleteFacture.fulfilled, (state, action) => {
      state.factures = state.factures.filter(f => f.id !== action.payload);
      state.impayees = state.impayees.filter(f => f.id !== action.payload);
    });
  },
});

export const { clearError, clearCurrentFacture } = facturesSlice.actions;
export default facturesSlice.reducer;
